"use client";

import { AppLink as Link, useAppPathname as usePathname } from "@/lib/navigation";
import {
  History,
  ListTodo,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from "@/components/ui/sidebar";
import { NavUser } from "./nav-user";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useTaskCountForCurrentUser,
  useTaskInitialLoad,
  useTaskList,
} from "@/stores/task-store";
import { CamundaUserService } from "@/lib/camunda";
import { countTasksVisibleToCurrentUser } from "./task-visibility";

const menuItems = [
  {
    title: "Iniciar Proceso",
    url: "/task-list/start-process",
    icon: PlusCircle,
  },
  {
    title: "Tareas",
    url: "/task-list/tasks", 
    icon: ListTodo,
  },
  {
    title: "Histórico",
    url: "/task-list/participated",
    icon: History,
  },
];

export function CamundaSidebar() {
  const { toggleSidebar, state, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";
  const pathname = usePathname();
  const isTaskListRoute = pathname === "/task-list/tasks" || pathname.startsWith("/task-list/tasks/");
  const tasks = useTaskList();
  const storeTaskCount = countTasksVisibleToCurrentUser(
    tasks,
    CamundaUserService.getCurrentUserId(),
  );
  const isTaskInitialLoad = useTaskInitialLoad();
  const fallbackTaskCount = useTaskCountForCurrentUser({ enabled: !isTaskListRoute });
  const taskCount = isTaskListRoute ? storeTaskCount : fallbackTaskCount.taskCount;
  const isCountLoading = isTaskListRoute
    ? isTaskInitialLoad && storeTaskCount === 0
    : fallbackTaskCount.isLoading;
  const taskCountLabel = taskCount > 99 ? "99+" : String(taskCount);
  const sidebarButtonLabel = isMobile
    ? "Cerrar menu"
    : isCollapsed
      ? "Expandir sidebar"
      : "Colapsar sidebar";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="relative">
        <div className={`flex items-center ${isCollapsed ? "justify-center h-16" : "justify-between p-4"}`}>
          {!isCollapsed && (
            <Link
              href="/task-list"
              prefetch={false}
              aria-label="Ir al inicio"
              className="flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
              onClick={() => {
                if (isMobile) toggleSidebar();
              }}
            >
              <img
                src="/brand/bpm-static.svg"
                alt="BPM"
                width={98}
                height={40}
                className="h-9 w-auto object-contain"
              />
            </Link>
          )}
          
          <Button
            onClick={toggleSidebar}
            variant="ghost"
            size="icon"
            className={`${isCollapsed ? "w-8 h-8" : ""} transition-all duration-200`}
            aria-label={sidebarButtonLabel}
          >
            {isMobile ? (
              <X className="h-4 w-4" />
            ) : isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Gestión de Tareas</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const isTasksItem = item.title === "Tareas";
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <Link
                        href={item.url}
                        prefetch={false}
                        className={isTasksItem ? "relative" : undefined}
                        aria-label={
                          isTasksItem
                            ? `Tareas, ${isCountLoading ? "cargando contador" : `${taskCount} disponibles`}`
                            : item.title
                        }
                        onClick={() => {
                          if (!isCollapsed) {
                            toggleSidebar();
                          }
                        }}
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="min-w-0 flex-1 truncate">
                          {item.title}
                        </span>
                        {isTasksItem && isCollapsed && (
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute right-0.5 top-0.5 z-30 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-neutral-950 px-1 text-[10px] font-semibold leading-none text-white shadow-sm ring-2 ring-sidebar tabular-nums"
                          >
                            {isCountLoading ? (
                              <span className="size-1.5 rounded-full bg-white/90 animate-pulse" />
                            ) : (
                              taskCountLabel
                            )}
                          </span>
                        )}
                        {isTasksItem && (
                          <Badge
                            variant="outline"
                            className="ml-auto min-w-6 rounded-md border-neutral-200 bg-neutral-100 px-1.5 py-0 text-[11px] font-semibold tabular-nums text-neutral-700 group-data-[collapsible=icon]:hidden"
                          >
                            {isCountLoading ? (
                              <span className="flex items-center gap-1" aria-label="Cargando contador de tareas">
                                <span className="size-1.5 rounded-full bg-neutral-500 animate-pulse" />
                                <span>...</span>
                              </span>
                            ) : (
                              taskCountLabel
                            )}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
